import { useCallback } from 'react'

import { get } from 'lodash'
import { useSelector } from 'react-redux'

import { useIsAuthed } from 'components/AuthShield'
import { FEATURE_NAMES } from 'components/ConfiguredDynamicFeatureProvider/features'
import { useIsDynamicFeatureEnabled } from 'components/DynamicFeatureProvider/use-is-dynamic-feature-enabled'
import { hasHighSchool } from 'store/user'

import { types } from '../../../../../AwesomeBar'
import ResultComponent from './ResultComponent'
import { keywordsArr, keywordsObj } from './keywords'
import weightedSort from './weighted-sort'

const RESULT_COUNT = 20

interface IFilteringData {
  hasHighSchool: boolean
}

interface IMatched {
  [key: string]: boolean
}

export const label = 'Pages'

export const search = (term: string, filteringData: IFilteringData) => {
  const sorted = weightedSort(term, keywordsArr, 50)
  return getTopResults(sorted, filteringData)
}

export const getTopResults = (sorted: any[], filteringData: IFilteringData) => {
  const matched: IMatched = {}
  const results = []
  let i = 0
  while (results.length < RESULT_COUNT) {
    const match = get(sorted[i], 'value')
    if (!match) {
      break
    }
    const result = get(keywordsObj, match)
    const id = get(result, 'id')
    const filteredBy = get(result, 'filteredBy')
    if (!matched[id] && (!filteredBy || filteredBy(filteringData))) {
      results.push({
        ...result,
        match,
        searchTypeLabel: label,
      })
      matched[id] = true
    }
    i++
  }
  return results
}

export const useSearch = (filteringData: IFilteringData) => {
  const searchPages = useCallback(
    (term: string) => {
      return new Promise((resolve) => {
        const results = search(term, filteringData)
        return resolve(Promise.resolve(results))
      })
    },
    [filteringData],
  )
  return searchPages
}

export const usePagesSearchConfig = (): types.ISearchConfiguration => {
  const isAuthed = useIsAuthed()
  const supportsMyColleges = useIsDynamicFeatureEnabled(FEATURE_NAMES.MyColleges)
  const supportEvents = useIsDynamicFeatureEnabled(FEATURE_NAMES.EventSearch)

  const filteringData = {
    hasHighSchool: useSelector(hasHighSchool),
    canShowMyColleges: supportsMyColleges,
    canShowApplyWithScoir: supportsMyColleges,
    canShowUpcomingEvents: supportEvents,
    canSupportSessions: supportEvents,
    canShowVisitsAndDeadlines: useIsDynamicFeatureEnabled(FEATURE_NAMES.Visits),
    canShowSurveys: useIsDynamicFeatureEnabled(FEATURE_NAMES.Surveys),
    canShowMyMatches: useIsDynamicFeatureEnabled(FEATURE_NAMES.PreferencesSearch),
    canShowMessages: useIsDynamicFeatureEnabled(FEATURE_NAMES.DirectMessages),
    canSupportMessages: useIsDynamicFeatureEnabled(FEATURE_NAMES.DirectMessages),
  }

  const search = useSearch(filteringData)
  return {
    label,
    search,
    ResultComponent,
    allLimit: 3,
    placeholderOption: 'Search for pages',
    hidden: !isAuthed,
  }
}
